/**
 * Agent catalogue hooks.
 *
 * The agents list is loaded once per session from the backend's
 * GET /agents endpoint. `useLoadAgents()` should be mounted near the top
 * of the app (e.g. inside AppRouter) so the slice is populated before any
 * consumer renders. `useAgents()` and `useAgentById()` are read-only and
 * safe to use anywhere.
 */
import { useCallback, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '@/stores/store';
import {
  setAgentsLoading,
  setAvailableAgents,
  setAgentsError,
} from '@/stores/slices/agent-slice';
import {
  selectAvailableAgents,
  selectAgentsLoading,
  selectAgentsLoaded,
  selectAgentsError,
  selectAgentById,
} from '@/stores/selectors/agent-selectors';
import { agentsService } from '@/services/agents.service';
import { useAuthContext } from '@/providers/auth-provider';

/**
 * Trigger a load of the agents catalogue on mount (idempotent: skipped if
 * already loaded or in-flight). Exposes a `refresh()` callback for cases
 * where the catalogue might change at runtime.
 */
export function useLoadAgents() {
  const dispatch = useAppDispatch();
  const loaded = useAppSelector(selectAgentsLoaded);
  const loading = useAppSelector(selectAgentsLoading);
  const { isAuthenticated } = useAuthContext();

  const refresh = useCallback(async () => {
    dispatch(setAgentsLoading(true));
    const [error, agents] = await agentsService.listAgents();
    if (error || !agents) {
      dispatch(
        setAgentsError(error?.message ?? 'Failed to load agents catalogue'),
      );
      return;
    }
    dispatch(setAvailableAgents(agents));
  }, [dispatch]);

  useEffect(() => {
    if (!isAuthenticated || loaded || loading) return;
    void refresh();
  }, [isAuthenticated, loaded, loading, refresh]);

  return { refresh };
}

/** Read the current catalogue + loading flags. Does NOT trigger a fetch. */
export function useAgents() {
  const agents = useAppSelector(selectAvailableAgents);
  const loading = useAppSelector(selectAgentsLoading);
  const loaded = useAppSelector(selectAgentsLoaded);
  const error = useAppSelector(selectAgentsError);
  return { agents, loading, loaded, error };
}

/** Resolve a single agent from the catalogue. Returns null until loaded. */
export function useAgentById(id: string | null | undefined) {
  return useAppSelector(selectAgentById(id));
}
